import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { X, RefreshCw, Camera, Video, Circle, Square, Loader2, AlertTriangle, Zap, ZapOff, Check, RotateCcw, Sparkles } from "lucide-react";

const MAX_SECS = 8;

const FILTERS = [
  { key: "none", label: "Orjinal", css: "none" },
  { key: "gray", label: "Gri", css: "grayscale(1)" },
  { key: "sepia", label: "Sepya", css: "sepia(0.8) saturate(1.1)" },
  { key: "bright", label: "Parlak", css: "brightness(1.2) contrast(1.05) saturate(1.15)" },
];

export default function InAppCamera({ onClose, onCapture }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const startedAtRef = useRef(0);
  const mimeRef = useRef("");
  const previewVideoRef = useRef(null);

  const [facing, setFacing] = useState("user");
  const [mode, setMode] = useState("photo");
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [starting, setStarting] = useState(true);
  const [error, setError] = useState("");
  const [processing, setProcessing] = useState(false);
  const [torchOn, setTorchOn] = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);
  const [filter, setFilter] = useState("none");
  const [filterOpen, setFilterOpen] = useState(false);
  const tileRefs = useRef({});
  const [streamReady, setStreamReady] = useState(false);

  // Preview state — after capture, hold the media so user can retake or use it
  const [preview, setPreview] = useState(null); // {dataUrl, mediaType, duration}

  const currentFilterCss = FILTERS.find((f) => f.key === filter)?.css || "none";

  // Keep the filter preview tiles' srcObject in sync with the active stream
  useEffect(() => {
    if (!streamReady || !streamRef.current) return;
    Object.values(tileRefs.current).forEach((el) => {
      if (!el) return;
      if (el.srcObject !== streamRef.current) {
        el.srcObject = streamRef.current;
        el.play().catch(() => {});
      }
    });
  }, [streamReady, filterOpen, facing]);

  const stopStream = useCallback(() => {
    try {
      streamRef.current?.getTracks().forEach((t) => t.stop());
    } catch {}
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    // Detach shared stream from filter preview tiles
    Object.values(tileRefs.current).forEach((el) => { if (el) el.srcObject = null; });
    setStreamReady(false);
    setTorchOn(false);
    setTorchSupported(false);
  }, []);

  const startStream = useCallback(async (facingMode, wantAudio) => {
    setStarting(true);
    setError("");
    stopStream();
    try {
      if (!navigator.mediaDevices?.getUserMedia) throw new Error("Tarayıcı kamera erişimini desteklemiyor");
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: facingMode },
          width: { ideal: 720 },
          height: { ideal: 1280 },
          // Nudge browsers with flexible cameras (many laptop webcams) toward
          // a 9:16 portrait crop instead of their native landscape.
          aspectRatio: { ideal: 9 / 16 },
        },
        audio: !!wantAudio,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        try { await videoRef.current.play(); } catch {}
      }
      setStreamReady(true);
      // Detect torch capability on the active video track
      const track = stream.getVideoTracks()[0];
      try {
        const caps = track.getCapabilities?.() || {};
        setTorchSupported(!!caps.torch);
      } catch { setTorchSupported(false); }
    } catch (e) {
      const msg = e?.name === "NotAllowedError"
        ? "Kamera izni reddedildi. Tarayıcı ayarlarından izin ver."
        : (e?.message || "Kamera açılamadı");
      setError(msg);
    }
    setStarting(false);
  }, [stopStream]);

  useEffect(() => {
    if (preview) return; // Freeze stream while previewing
    startStream(facing, mode === "video");
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      stopStream();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facing, mode, preview]);

  const toggleTorch = async () => {
    const track = streamRef.current?.getVideoTracks?.()[0];
    if (!track) return;
    const next = !torchOn;
    try {
      await track.applyConstraints({ advanced: [{ torch: next }] });
      setTorchOn(next);
    } catch {
      setTorchSupported(false);
    }
  };

  const capturePhoto = useCallback(() => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    setProcessing(true);
    const canvas = document.createElement("canvas");
    canvas.width = v.videoWidth;
    canvas.height = v.videoHeight;
    const ctx = canvas.getContext("2d");
    // Bake filter into the JPEG so what user saw is what they get
    try { ctx.filter = currentFilterCss; } catch {}
    if (facing === "user") {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }
    ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
    setProcessing(false);
    setPreview({ dataUrl, mediaType: "image", duration: null });
  }, [facing, currentFilterCss]);

  const pickMime = () => {
    const options = [
      "video/webm;codecs=vp9,opus",
      "video/webm;codecs=vp8,opus",
      "video/webm",
      "video/mp4;codecs=avc1,aac",
      "video/mp4",
    ];
    if (typeof MediaRecorder === "undefined") return "";
    for (const m of options) {
      try { if (MediaRecorder.isTypeSupported(m)) return m; } catch {}
    }
    return "";
  };

  const stopRecording = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    const r = recorderRef.current;
    if (r && r.state === "recording") r.stop();
    setRecording(false);
  }, []);

  const startRecording = useCallback(() => {
    if (!streamRef.current) return;
    if (typeof MediaRecorder === "undefined") {
      setError("Tarayıcı video kaydını desteklemiyor");
      return;
    }
    chunksRef.current = [];
    const mime = pickMime();
    mimeRef.current = mime;
    let recorder;
    try {
      recorder = new MediaRecorder(streamRef.current, mime ? { mimeType: mime, videoBitsPerSecond: 2_500_000 } : undefined);
    } catch (e) {
      setError("Kaydedici başlatılamadı: " + (e?.message || ""));
      return;
    }
    recorder.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
    recorder.onstop = () => {
      const type = mimeRef.current || "video/webm";
      const blob = new Blob(chunksRef.current, { type });
      const durationSec = Math.min(MAX_SECS, (Date.now() - startedAtRef.current) / 1000);
      setProcessing(true);
      const reader = new FileReader();
      reader.onload = () => {
        setProcessing(false);
        setPreview({ dataUrl: String(reader.result), mediaType: "video", duration: durationSec });
      };
      reader.onerror = () => {
        setProcessing(false);
        setError("Video işlenemedi");
      };
      reader.readAsDataURL(blob);
    };
    recorderRef.current = recorder;
    recorder.start(120);
    startedAtRef.current = Date.now();
    setElapsed(0);
    setRecording(true);
    timerRef.current = setInterval(() => {
      const secs = (Date.now() - startedAtRef.current) / 1000;
      setElapsed(secs);
      if (secs >= MAX_SECS) {
        stopRecording();
      }
    }, 80);
  }, [stopRecording]);

  const onShutter = () => {
    if (starting || error || processing) return;
    if (mode === "photo") return capturePhoto();
    if (recording) return stopRecording();
    return startRecording();
  };

  const cancel = () => {
    if (recording) stopRecording();
    if (timerRef.current) clearInterval(timerRef.current);
    stopStream();
    onClose();
  };

  const retake = () => {
    setPreview(null);
    setElapsed(0);
  };

  const usePreview = () => {
    if (!preview) return;
    stopStream();
    onCapture(preview);
  };

  const flip = () => {
    if (recording || preview) return;
    setFacing((f) => (f === "user" ? "environment" : "user"));
  };

  const switchMode = (m) => {
    if (recording || preview) return;
    setMode(m);
  };

  // Progress ring math
  const RING_R = 34;
  const RING_C = 2 * Math.PI * RING_R;
  const progress = Math.min(1, elapsed / MAX_SECS);
  const dashOffset = RING_C * (1 - progress);

  // ------------------------------------------------------------------------
  // PREVIEW MODE (post-capture)
  // ------------------------------------------------------------------------
  if (preview) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[95] bg-black text-white flex flex-col"
        data-testid="camera-preview-view"
      >
        <div
          className="relative z-10 flex items-center justify-between p-3"
          style={{ paddingTop: "calc(0.75rem + env(safe-area-inset-top, 0px))" }}
        >
          <button
            onClick={retake}
            data-testid="camera-retake"
            className="bg-white/15 backdrop-blur border-2 border-white/40 rounded-xl px-3 py-2 font-black text-xs uppercase tracking-widest press-btn flex items-center gap-2"
          >
            <RotateCcw strokeWidth={3} className="w-4 h-4" /> Yeniden çek
          </button>
          <div className="text-[11px] uppercase font-black tracking-widest bg-white/15 backdrop-blur border-2 border-white/40 rounded-full px-3 py-1.5">
            Önizleme
          </div>
          <button
            onClick={cancel}
            data-testid="camera-preview-close"
            className="w-11 h-11 bg-white/15 backdrop-blur border-2 border-white/40 rounded-xl flex items-center justify-center press-btn"
            aria-label="Kapat"
          >
            <X strokeWidth={3} className="w-5 h-5" />
          </button>
        </div>

        <div className="relative flex-1 bg-black overflow-hidden">
          {preview.mediaType === "video" ? (
            <video
              ref={previewVideoRef}
              src={preview.dataUrl}
              className="absolute inset-0 w-full h-full object-contain"
              autoPlay
              loop
              muted={false}
              playsInline
              controls={false}
              data-testid="camera-preview-video"
            />
          ) : (
            <img src={preview.dataUrl} alt="" className="absolute inset-0 w-full h-full object-contain" data-testid="camera-preview-image" />
          )}
          {preview.mediaType === "video" && preview.duration && (
            <div className="absolute bottom-3 left-3 text-[10px] uppercase font-black tracking-widest bg-white/90 text-[#0A0A0A] border-hard-2 rounded-full px-2 py-0.5">
              {preview.duration.toFixed(1)}sn
            </div>
          )}
        </div>

        <div
          className="relative z-10 p-4"
          style={{ paddingBottom: "calc(1.25rem + env(safe-area-inset-bottom, 0px))" }}
        >
          <button
            onClick={usePreview}
            data-testid="camera-use"
            className="w-full bg-[#CCFF00] text-[#0A0A0A] border-hard shadow-hard rounded-xl px-5 py-3.5 font-black text-base press-btn flex items-center justify-center gap-2"
          >
            <Check strokeWidth={3} className="w-5 h-5" /> Bunu kullan
          </button>
        </div>
      </motion.div>
    );
  }

  // ------------------------------------------------------------------------
  // LIVE MODE
  // ------------------------------------------------------------------------
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[95] bg-black text-white flex flex-col"
      data-testid="in-app-camera"
    >
      {/* Top bar */}
      <div
        className="relative z-10 flex items-center justify-between p-3"
        style={{ paddingTop: "calc(0.75rem + env(safe-area-inset-top, 0px))" }}
      >
        <button
          onClick={cancel}
          data-testid="camera-close"
          className="w-11 h-11 bg-white/15 backdrop-blur border-2 border-white/40 rounded-xl flex items-center justify-center press-btn"
          aria-label="Kapat"
        >
          <X strokeWidth={3} className="w-5 h-5" />
        </button>

        {recording ? (
          <div className="flex items-center gap-2 bg-[#FF3B30] border-hard rounded-full px-3 py-1.5 shadow-hard-sm">
            <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
            <span className="font-black text-sm">{elapsed.toFixed(1)} / {MAX_SECS}s</span>
          </div>
        ) : (
          <div className="text-[11px] uppercase font-black tracking-widest bg-white/15 backdrop-blur border-2 border-white/40 rounded-full px-3 py-1.5" data-testid="camera-mode-label">
            {mode === "photo" ? "FOTO" : `VİDEO · MAKS ${MAX_SECS}sn`}
          </div>
        )}

        <div className="flex items-center gap-2">
          {torchSupported && !recording && (
            <button
              onClick={toggleTorch}
              data-testid="camera-torch"
              className={`w-11 h-11 backdrop-blur border-2 rounded-xl flex items-center justify-center press-btn ${torchOn ? "bg-[#FFF7C2] text-[#0A0A0A] border-[#0A0A0A]" : "bg-white/15 border-white/40"}`}
              aria-label={torchOn ? "Flaşı kapat" : "Flaşı aç"}
            >
              {torchOn ? <Zap strokeWidth={3} className="w-5 h-5" /> : <ZapOff strokeWidth={3} className="w-5 h-5" />}
            </button>
          )}
          <button
            onClick={flip}
            disabled={recording}
            data-testid="camera-flip"
            className="w-11 h-11 bg-white/15 backdrop-blur border-2 border-white/40 rounded-xl flex items-center justify-center press-btn disabled:opacity-40"
            aria-label="Kamerayı çevir"
          >
            <RefreshCw strokeWidth={3} className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Live preview */}
      <div className="relative flex-1 overflow-hidden bg-black">
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          style={{ filter: currentFilterCss }}
          className={`absolute inset-0 w-full h-full object-cover ${facing === "user" ? "transform scale-x-[-1]" : ""}`}
          data-testid="camera-preview"
        />

        {starting && !error && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/70 gap-2 text-sm" data-testid="camera-starting">
            <Loader2 className="w-5 h-5 animate-spin" /> Kamera başlatılıyor…
          </div>
        )}

        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/85 p-6" data-testid="camera-error">
            <div className="bg-white text-[#0A0A0A] border-hard shadow-hard rounded-2xl p-4 max-w-sm w-full text-center">
              <AlertTriangle strokeWidth={3} className="w-6 h-6 mx-auto mb-2" />
              <div className="font-black text-base mb-1">Kamera açılamadı</div>
              <div className="text-xs text-[#555]">{error}</div>
              <button
                onClick={cancel}
                data-testid="camera-error-close"
                className="mt-3 bg-[#0A0A0A] text-white font-black rounded-xl px-4 py-2 text-sm press-btn"
              >
                Geri dön
              </button>
            </div>
          </div>
        )}

        {processing && !error && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/70 gap-2 text-sm" data-testid="camera-processing">
            <Loader2 className="w-5 h-5 animate-spin" /> Hazırlanıyor…
          </div>
        )}
      </div>

      {/* Filter strip (above the shutter) — 60x60 live preview tiles */}
      {!recording && (
        <div className="relative z-10 px-3 pt-2">
          <button
            onClick={() => setFilterOpen((s) => !s)}
            data-testid="camera-filter-toggle"
            className={`w-10 h-10 backdrop-blur border-2 rounded-xl flex items-center justify-center press-btn mb-2 ${filter !== "none" ? "bg-[#CCFF00] text-[#0A0A0A] border-[#0A0A0A]" : "bg-white/15 border-white/40"}`}
            aria-label="Filtre"
          >
            <Sparkles strokeWidth={3} className="w-4 h-4" />
          </button>
          {filterOpen && (
            <div className="flex gap-2 overflow-x-auto pb-1" data-testid="camera-filter-strip">
              {FILTERS.map((f) => {
                const active = filter === f.key;
                return (
                  <button
                    key={f.key}
                    onClick={() => setFilter(f.key)}
                    data-testid={`camera-filter-${f.key}`}
                    className="shrink-0 flex flex-col items-center gap-1 press-btn"
                    aria-label={f.label}
                  >
                    <div className={`w-[60px] h-[60px] rounded-xl overflow-hidden border-2 ${active ? "border-[#CCFF00] shadow-hard-sm" : "border-white/40"}`}>
                      {streamReady ? (
                        <video
                          ref={(el) => {
                            if (!el) return;
                            tileRefs.current[f.key] = el;
                            if (streamRef.current && el.srcObject !== streamRef.current) {
                              el.srcObject = streamRef.current;
                              el.play().catch(() => {});
                            }
                          }}
                          autoPlay
                          muted
                          playsInline
                          style={{ filter: f.css }}
                          className={`w-full h-full object-cover ${facing === "user" ? "transform scale-x-[-1]" : ""}`}
                          data-testid={`camera-filter-tile-${f.key}`}
                        />
                      ) : (
                        <div className="w-full h-full bg-white/10" />
                      )}
                    </div>
                    <span className={`text-[9px] uppercase font-black tracking-tight ${active ? "text-[#CCFF00]" : "text-white/80"}`}>{f.label}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Bottom bar */}
      <div
        className="relative z-10 pt-3"
        style={{ paddingBottom: "calc(1.5rem + env(safe-area-inset-bottom, 0px))" }}
      >
        <div className="flex items-center justify-center gap-3 mb-4">
          <button
            onClick={() => switchMode("photo")}
            data-testid="camera-mode-photo"
            disabled={recording}
            className={`px-3 py-1.5 rounded-full border-2 font-black text-xs uppercase tracking-widest press-btn disabled:opacity-40 flex items-center gap-1.5 ${mode === "photo" ? "bg-[#CCFF00] text-[#0A0A0A] border-[#0A0A0A]" : "bg-white/15 backdrop-blur text-white border-white/40"}`}
          >
            <Camera strokeWidth={3} className="w-3.5 h-3.5" /> Foto
          </button>
          <button
            onClick={() => switchMode("video")}
            data-testid="camera-mode-video"
            disabled={recording}
            className={`px-3 py-1.5 rounded-full border-2 font-black text-xs uppercase tracking-widest press-btn disabled:opacity-40 flex items-center gap-1.5 ${mode === "video" ? "bg-[#CCFF00] text-[#0A0A0A] border-[#0A0A0A]" : "bg-white/15 backdrop-blur text-white border-white/40"}`}
          >
            <Video strokeWidth={3} className="w-3.5 h-3.5" /> Video
          </button>
        </div>

        <div className="flex items-center justify-center relative">
          <button
            onClick={onShutter}
            data-testid="camera-shutter"
            disabled={starting || !!error || processing}
            aria-label={mode === "photo" ? "Foto çek" : (recording ? "Durdur" : "Kaydı başlat")}
            className="relative w-20 h-20 rounded-full border-4 border-white bg-transparent flex items-center justify-center press-btn disabled:opacity-40"
          >
            {mode === "video" && (
              <svg className="absolute inset-0 -rotate-90" width="80" height="80" viewBox="0 0 80 80" aria-hidden>
                <circle cx="40" cy="40" r={RING_R} fill="none" stroke="#0A0A0A" strokeOpacity="0.25" strokeWidth="6" />
                <circle
                  cx="40"
                  cy="40"
                  r={RING_R}
                  fill="none"
                  stroke="#FF3B30"
                  strokeWidth="6"
                  strokeLinecap="round"
                  strokeDasharray={RING_C}
                  strokeDashoffset={dashOffset}
                  style={{ transition: "stroke-dashoffset 80ms linear" }}
                />
              </svg>
            )}
            {mode === "photo" ? (
              <span className="w-14 h-14 rounded-full bg-white" />
            ) : recording ? (
              <span className="w-8 h-8 rounded-md bg-[#FF3B30] flex items-center justify-center">
                <Square strokeWidth={0} fill="#FFFFFF" className="w-4 h-4" />
              </span>
            ) : (
              <span className="w-14 h-14 rounded-full bg-[#FF3B30] flex items-center justify-center">
                <Circle strokeWidth={0} fill="#FFFFFF" className="w-6 h-6" />
              </span>
            )}
          </button>
        </div>

        <div className="text-center mt-3 text-[11px] uppercase font-black tracking-widest text-white/70" data-testid="camera-hint">
          {mode === "photo" ? "Fotoğraf çekmek için basın" : (recording ? `Otomatik olarak ${MAX_SECS}sn'de durur` : "Kaydı başlatmak için basın")}
        </div>
      </div>
    </motion.div>
  );
}
